import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { OptionType } from '../../../../util/types';
import { repository } from '@whizz/api-client';
import { useQuery } from 'react-query';
import { ButtonIcon, ChoiceGroup, Table } from '../../../../components';
import { SelectionActionControl } from '../../../../components/selection-action/SelectionActionControl';
import type { SelectionAction } from '../../../../components/selection-action/SelectionActionControl';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import Loader from '../../../../components/loader/Loader';
import { DateTime } from 'luxon';
import { getNextWeekday } from '../../../../util/dates';
import { useI18n } from '@whizz/react-i18n';
import { useLocalStorage } from 'react-use';
import { throttle } from 'lodash';
import { TableFilters } from './TableFilters';
import ChangeOrganizationPausedUntilModal from './ChangeOrganizationPausedUntilModal';
import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_next_send_date'],
    [data-column='cell_next_send_date'] {
      margin-left: 10px;
      position: relative;
      background: inherit;
      z-index: 2;
      &:before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -14px;
        bottom: -14px;
        z-index: -1;
        pointer-events: none;
        box-shadow: -14px 0 14px -14px rgba(0,0,0,.35);
      }
    }
    [data-id='col_change_next_send_date'],
    [data-column='cell_change_next_send_date'] {
      position: relative;
      background: inherit;
      z-index: 2;
      text-align: center;
      &:after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -14px;
        bottom: -14px;
        z-index: -1;
        pointer-events: none;
        box-shadow: 14px 0 14px -14px rgba(0,0,0,.35);
      }
    }
    [data-id='col_change_next_send_date'] > div {
      display: flex;
      justify-content: center;
      width: 100%;
    }
    .leader-title {
      position: relative;
      background: inherit;
      z-index: 10;
      margin-left: 10px;
      &:before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -14px;
        bottom: -14px;
        z-index: -1;
        pointer-events: none;
        box-shadow: -14px 0 14px -14px rgba(0,0,0,.35);
      }
    }
  }
`;

const OrganizationsTable = ({ onClickEdit }: Props): JSX.Element => {
  const { translate: t } = useI18n();

  const [ selectedOrganizations, setSelectedOrganizations ] = useState<number[]>([]);
  const [ modalsData, setModalsData ] = useState<ModalsData>({ action: null });
  const [ search, setSearch ] = useLocalStorage<string>('admin_org_search', '');
  const [ filterExtraData, setExtraData ] = useLocalStorage<{ country: OptionType<string> | null }>('admin_org_filter', { country: null });
  const [ pageIndex, setPageIndex ] = useLocalStorage<number>('admin_org_page_index', 0);

  const safeFilterExtraData = filterExtraData ?? { country: null };
  const safeSetExtraData = setExtraData ?? (() => undefined);
  const safeSetPageIndex = useMemo(
    () => setPageIndex ?? (() => undefined),
    [ setPageIndex ],
  );
  const throttledResetPageIndex = useMemo(
    () => throttle(() => safeSetPageIndex(0), 500),
    [ safeSetPageIndex ],
  );
  const hasMounted = useRef(false);

  const getOrganizations = useQuery({
    queryKey: QUERIES_IDS.ADMIN.GET_ORGANISATIONS,
    queryFn: () => repository.AdminRepository.getOrganizations(),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return undefined;
    }
    throttledResetPageIndex();
    return () => throttledResetPageIndex.cancel();
  }, [ search, safeFilterExtraData.country?.value ]);

  if (getOrganizations.isLoading) {
    return <Loader />;
  }

  const allOrganizations = getOrganizations.data?.data ?? [];

  const countryOptions: OptionType[] = Array.from(
    new Set(allOrganizations.map((org) => org.organization.country).filter(Boolean)),
  ).map((code) => ({ label: t(`countries.COUNTRY_${code}`), value: code }));

  const organizations = allOrganizations.filter((org) => {
    if (safeFilterExtraData.country?.value && org.organization.country !== safeFilterExtraData.country.value) {
      return false;
    }
    if (!search) {
      return true;
    }
    const q = search.toLowerCase();
    if (org.organization.name.toLowerCase().includes(q)) {
      return true;
    }
    return org.schools.some(
      (s) => s.name.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q) || s.postcode?.toLowerCase().includes(q),
    );
  });

  const organizationsWithNextSendDate = organizations.filter((org) => org.isEnabled);
  const selectedVisibleCount = organizationsWithNextSendDate
    .filter((org) => selectedOrganizations.includes(org.organization.id))
    .length;

  const selectionActions: SelectionAction[] = selectedOrganizations.length > 0 ? [
    {
      key: 'change_next_send_date',
      count: selectedOrganizations.length,
      description: `${selectedOrganizations.length === 1 ? 'organization' : 'organizations'} selected`,
      buttonLabel: t('pages.ADMIN.TABLE.HEADERS.CHANGE_NEXT_SEND_DATE') ?? '',
      onClick: () => setModalsData({ action: ModalsAction.changeNextSendDate, organizations: selectedOrganizations }),
    },
  ] : [];

  function modals() {
    if (modalsData.action === ModalsAction.changeNextSendDate) {
      return (
        <ChangeOrganizationPausedUntilModal
          toggle={() => setModalsData({ action: null })}
          organizations={modalsData.organizations}
          onSuccess={() => setSelectedOrganizations([])}
        />
      );
    }
    return null;
  }

  return (
    <>
      {modals()}
      <SelectionActionControl actions={selectionActions} />
      <div className={`flex flex-col gap-5 ${selectionActions.length ? 'pb-32' : ''}`}>
        <GlobalStyle />
        <Table
          id={'organization_weekly_leaders_emails'}
          customHeaderComponent={<TableFilters
            search={search ?? ''}
            onSearchChange={setSearch}
            country={safeFilterExtraData.country}
            onCountryChange={(option) => safeSetExtraData({ ...safeFilterExtraData, country: option })}
            countryOptions={countryOptions}
            searchColSpan={5}
            labelColSpan={2}
            searchPlaceholder={t('pages.ADMIN.ORGANIZATIONS.SEARCH_PLACEHOLDER')}
          />}
          cols={[
            {
              id: 'edit',
              Header: t('pages.ADMIN.ORGANIZATIONS.TABLE.HEADERS.EDIT'),
              accessor: 'organization.id',
              Cell: (props) => (
                <ButtonIcon
                  icon={GLOBAL_ICONS.edit}
                  size={'sm'}
                  isOutline
                  onClick={() => onClickEdit(props.row.original.organization.id)}
                />
              ),
              disableSortBy: true,
            },
            {
              id: 'id',
              Header: t('pages.ADMIN.ORGANIZATIONS.TABLE.HEADERS.ID'),
              accessor: 'organization.id',
            },
            {
              Header: t('pages.ADMIN.ORGANIZATIONS.TABLE.HEADERS.NAME'),
              accessor: 'organization.name',
            },
            {
              Header: t('pages.ADMIN.ORGANIZATIONS.TABLE.HEADERS.TYPE'),
              id: 'type',
              accessor: (original) => t(`pages.ADMIN.ORGANIZATIONS.TYPES.TYPE_${original.organization.typeId}`),
            },
            {
              Header: t('pages.ADMIN.ORGANIZATIONS.TABLE.HEADERS.NO_OF_SCHOOLS'),
              id: 'school_count',
              accessor: (original) => original.schools.length,
            },
            {
              Header: t('pages.ADMIN.ORGANIZATIONS.TABLE.HEADERS.COUNTRY'),
              id: 'country',
              accessor: (original) => t(`countries.COUNTRY_${original.organization.country}`),
            },
            {
              Header: t('pages.ADMIN.ORGANIZATIONS.TABLE.HEADERS.NEXT_SEND_DATE'),
              id: 'next_send_date',
              accessor: (original) => {
                if (!original.isEnabled || null == original.weekday || null == original.hour) {
                  return null;
                }

                const now = DateTime.now().setZone(original.organization.timezone);

                let dateFrom = now;

                if (null != original.pausedUntil) {
                  const pausedUntil = DateTime.fromISO(original.pausedUntil);
                  if (now < pausedUntil) {
                    dateFrom = pausedUntil;
                  }
                }

                return getNextWeekday(original.weekday, original.hour, dateFrom).toLocaleString();
              },
              Cell: ({ cell }) => {
                if (null === cell.value) {
                  return <span className={'text-muted italic'}>Disabled</span>;
                }
                return cell.value;
              },
            },
            {
              Header: () => (
                <div className='flex justify-center'>
                  <ChoiceGroup
                    id={'change_all_orgs'}
                    label={''}
                    hideLabel
                    checked={selectedVisibleCount === organizationsWithNextSendDate.length && organizationsWithNextSendDate.length > 0}
                    onChange={() => setSelectedOrganizations(() => {
                      if (selectedVisibleCount === organizationsWithNextSendDate.length) {
                        return [];
                      }
                      return organizationsWithNextSendDate.map((org) => org.organization.id);
                    })}
                    disabled={null == safeFilterExtraData.country?.value}
                  />
                </div>
              ),
              id: 'change_next_send_date',
              accessor: 'organization.id',
              Cell: ({ row }) => {
                const original = row.original;
                const orgId = original.organization.id;

                return (
                  <div className='flex justify-center'>
                    <ChoiceGroup
                      id={`change_org_${orgId}`}
                      label={''}
                      hideLabel
                      checked={selectedOrganizations.includes(orgId)}
                      onChange={() => setSelectedOrganizations((prev) => {
                        if (prev.includes(orgId)) {
                          return prev.filter((id) => id !== orgId);
                        }
                        return [ ...prev, orgId ];
                      })}
                      disabled={!original.isEnabled}
                    />
                  </div>
                );
              },
              disableSortBy: true,
            },
          ]}
          content={organizations}
          noDataMessage={'No organizations found'}
          initialState={{ pageSize: 50, pageIndex: pageIndex ?? 0 }}
          onPageIndexChange={setPageIndex}
        />
      </div>
    </>
  );
};

interface Props {
  onClickEdit: (organizationId: number) => void;
}

enum ModalsAction {
  changeNextSendDate,
}

type ModalsData =
  | { action: ModalsAction.changeNextSendDate; organizations: number[] }
  | { action: null };

export { OrganizationsTable };
